// Scene 3 - Precheck + Artefakt-Diff + globale Referenzen.
// Startet den Precheck, sobald der Bucket-Set (Werke + Package) sich aendert.
// Baut die Zeilen aus state.precheckByBucket auf.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "../i18n";
import { useTransportV2, hasWerke, buckets, visBuckets, activeWerke, gewaehlteIflows, refNutzer, refGebraucht, autoDeploy, referenceAutoDeploy, referenceDeployRequired } from "../context/TransportV2Context";
import DeploymentSwitch from "../components/DeploymentSwitch";
import { Cx } from "../components/IconSprite";
import { usePipelineReady } from "../hooks/usePipelineReady";
import { usePrecheckStream } from "../hooks/usePrecheckStream";
import { api } from "../../../utils/helpers";
import BpmnCompareDialog from "../../../components/BpmnCompareDialog";

function mapStatus(s) {
  if (s === "new") return "new";
  if (s === "update") return "update";
  if (s === "identical") return "identical";
  if (s === "draft") return "draft";
  if (s === "orphan") return "orphan";
  return s || "identical";
}

const ST_CLS = { new: "ok", update: "info", identical: "idle", draft: "warn", orphan: "warn" };

// Rang einer Referenz in der SAP-Deploy-Reihenfolge. Zuerst der Typ-Schluessel (typ), als
// Rueckfall das Label (Type) fuer Antworten eines aelteren Backends ohne typ.
const REF_TYP_RANG = { messagemapping: 0, scriptcollection: 1, valuemapping: 2 };
function refTypRang(r) {
  const key = String(r?.typ || r?.type || "").replace(/\s+/g, "").toLowerCase();
  const rang = REF_TYP_RANG[key];
  return rang === undefined ? 3 : rang;
}

function cmpVer(a, b) {
  const A = String(a || "").split(".").map((n) => parseInt(n, 10) || 0);
  const B = String(b || "").split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(A.length, B.length); i++) {
    const d = (A[i] || 0) - (B[i] || 0);
    if (d) return d;
  }
  return 0;
}

// Beide Tabellen teilen sich Spaltenbreiten und Zellabstände.
function ArtifactColumns() {
  return <colgroup><col className="transport-col-choice" /><col /><col className="transport-col-versions" />
    <col className="transport-col-status" /><col className="transport-col-deploy" /><col className="transport-col-action" /></colgroup>;
}

function Versions({ source, target }) {
  const { t } = useLang();
  return <span className="transport-versions"><span title={t("th.src")}>{source || "-"}</span>
    <Cx /><span title={t("th.tgt")}>{target || "-"}</span></span>;
}

export default function Scene3Artifacts() {
  const { t } = useLang();
  const { state, dispatch } = useTransportV2();
  const stream = usePrecheckStream();
  const pipeline = usePipelineReady(state.spaceId);
  const deploymentAvailable = !pipeline.loading && pipeline.ready && pipeline.modus !== "sap";
  const [modal, setModal] = useState(null);
  const [release, setRelease] = useState(null);
  const startedRef = useRef(null);
  // Erzwingt einen frischen Precheck-Lauf (der Effekt unten haengt daran): die
  // Bucket-Signatur allein aendert sich nicht, wenn nur der iFlow-INHALT in der
  // Quelle geaendert wurde.
  const [reloadNonce, setReloadNonce] = useState(0);
  const [reloadPending, setReloadPending] = useState(false);
  const reloadSelectionRef = useRef(null);

  const bucketList = buckets(state);
  const visible = visBuckets(state);
  const withWerke = hasWerke(state);

  // Precheck starten wenn Bucket-Signatur wechselt
  useEffect(() => {
    if (state.step !== 3) return;
    if (!state.sourceInstanceId || !state.pkgId) return;
    const sig = state.spaceKey + "|" + state.pkgId + "|" + bucketList.map((b) => b.key).join(",");
    if (startedRef.current === sig) return;
    startedRef.current = sig;
    stream.reset();
    const werke = withWerke ? bucketList.map((b) => ({ key: b.key, kennung: b.kennung, name: b.name })) : [];
    const targetInst = state.space?.qasInstance;
    if (!targetInst) return;
    stream.run({
      sourceInstanceId: state.sourceInstanceId,
      sourcePackageId: state.pkgId,
      targetInstances: [targetInst],
      werke,
    }).catch(() => {}).finally(() => setReloadPending(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step, state.pkgId, state.spaceKey, state.werkOn, reloadNonce]);

  // Die letzte Liste bleibt sichtbar, bis der neue Vergleich vorliegt.
  // Ein Ref allein startet keinen Effekt; reloadNonce ist der Auslöser.
  const refresh = useCallback((releasedId = null) => {
    startedRef.current = null;
    reloadSelectionRef.current = { sel: state.sel, releasedId: releasedId || (stream.error ? reloadSelectionRef.current?.releasedId : null) };
    setReloadPending(true);
    setReloadNonce((n) => n + 1);
  }, [state.sel, stream.error]);

  // Precheck-Ergebnis in Context spiegeln + Selektion vor-belegen
  useEffect(() => {
    if (!stream.precheck) return;
    if (Object.keys(stream.precheck).length === 0) return;
    dispatch({ type: "SET_PRECHECK", payload: stream.precheck });
    dispatch({ type: "SET_GLOBAL_REFS", payload: stream.refs || [] });
    // Vor-Selektion pro Werk: alles ausser identisch/draft
    const sel = {};
    const expanded = {};
    for (const entry of Object.values(stream.precheck)) {
      if (!entry || !Array.isArray(entry.comparison)) continue;
      const werkKey = entry.werk?.kennung || "std";
      for (const a of entry.comparison) {
        const src = a.originalId || a.Id;
        if (!sel[src]) sel[src] = {};
        expanded[src] = true;
        const status = mapStatus(a.status);
        const saved = reloadSelectionRef.current;
        const wasSelected = !saved || saved.releasedId === src || !Object.prototype.hasOwnProperty.call(saved.sel || {}, src)
          ? true : !!saved.sel[src]?.[werkKey];
        if (status !== "identical" && !a.draft && wasSelected) sel[src][werkKey] = true;
      }
    }
    dispatch({ type: "SET_SEL", payload: sel });
    dispatch({ type: "SET_EXPANDED", payload: expanded });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.precheck, stream.refs]);

  // Aggregierte Zeilen aus precheck aufbauen
  const rows = useMemo(() => {
    const cmp = state.precheckByBucket || {};
    const byOrigId = new Map();
    for (const [, entry] of Object.entries(cmp)) {
      if (!entry || !Array.isArray(entry.comparison)) continue;
      const werkKey = entry.werk?.kennung || "std";
      const werk = entry.werk ? { key: werkKey, name: entry.werk.name, kennung: entry.werk.kennung } : { key: "std", name: "Standard", kennung: "" };
      for (const a of entry.comparison) {
        const origId = a.originalId || a.Id;
        if (!byOrigId.has(origId)) {
          byOrigId.set(origId, {
            id: origId,
            name: a.Name || origId,
            type: a.Type || "Integration Flow",
            srcVer: a.Version || "-",
            draft: !!a.draft,
            lastVer: a.lastVer || a.Version || "1.0.0",
            werkeStatus: {},
            werke: [],
          });
        }
        const target = byOrigId.get(origId);
        target.werkeStatus[werkKey] = { status: mapStatus(a.status), tgt: a.targetVersion || null, suffixedId: a.suffixedId || a.Id, instanceId: entry.instance?.id };
        target.werke.push({ ...werk, ...target.werkeStatus[werkKey] });
      }
    }
    // sort: draft ganz nach oben, dann alphabetisch
    return [...byOrigId.values()].sort((a, b) => {
      if (a.draft !== b.draft) return a.draft ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [state.precheckByBucket]);

  const selectedRowsCount = useMemo(() => {
    let n = 0;
    for (const [src, werkSel] of Object.entries(state.sel || {})) {
      for (const b of bucketList) {
        if (werkSel && werkSel[b.key]) n++;
        void src;
      }
    }
    return n;
  }, [state.sel, bucketList]);

  const counts = useMemo(() => {
    const c = { new: 0, update: 0, identical: 0, draft: 0 };
    for (const row of rows) {
      if (row.draft) { c.draft += 1; continue; }
      for (const b of visible) {
        const info = row.werkeStatus[b.key];
        if (info) c[info.status] = (c[info.status] || 0) + 1;
      }
    }
    return c;
  }, [rows, visible]);

  const openModal = (row, werkKey) => {
    const info = row.werkeStatus[werkKey];
    if (!info) return;
    setModal({
      artId: row.id,
      artName: row.name,
      sourceInstanceId: state.sourceInstanceId,
      targetInstanceId: info.instanceId,
      targetArtId: info.suffixedId,
      srcVer: row.srcVer,
      tgtVer: info.tgt,
    });
  };

  const openRelease = useCallback(async (row) => {
    setRelease({ artId: row.id, loading: "info", version: "", floor: null, error: null });
    try {
      const d = await api(`/api/${state.sourceInstanceId}/transport/artifact-last-version/${encodeURIComponent(row.id)}?packageId=${encodeURIComponent(state.pkgId)}`);
      setRelease((p) => (p && p.artId === row.id ? { ...p, loading: null, floor: d.floor || null, version: d.suggested || d.lastVersion || row.lastVer || "1.0.1" } : p));
    } catch (e) {
      setRelease((p) => (p && p.artId === row.id ? { ...p, loading: null, version: row.lastVer || "1.0.1", error: e.message } : p));
    }
  }, [state.sourceInstanceId, state.pkgId]);

  const doRelease = useCallback(async (row) => {
    const r = release;
    if (!r || r.artId !== row.id) return;
    const ver = (r.version || "").trim();
    if (!/^\d+(\.\d+){1,3}$/.test(ver)) { setRelease({ ...r, error: "Ungültiges Format" }); return; }
    if (r.floor && cmpVer(ver, r.floor) <= 0) { setRelease({ ...r, error: `Version muss größer als ${r.floor} sein` }); return; }
    setRelease({ ...r, loading: "release", error: null });
    try {
      await api(`/api/${state.sourceInstanceId}/transport/release-version`, {
        method: "POST",
        body: JSON.stringify({ flowId: row.id, version: ver, packageId: state.pkgId, floor: r.floor || null }),
      });
      setRelease(null);
      refresh(row.id);
    } catch (e) {
      setRelease((p) => (p && p.artId === row.id ? { ...p, loading: null, error: e.message } : p));
    }
  }, [release, state.sourceInstanceId, state.pkgId, refresh]);

  const nSelectable = (row) => visible.filter((b) => {
    const info = row.werkeStatus[b.key];
    return info && info.status !== "identical";
  }).length;
  const nOn = (row) => visible.filter((b) => state.sel?.[row.id]?.[b.key]).length;

  const onToggle = (rowId, werkKey, on) => dispatch({ type: "TOGGLE_SEL", payload: { sourceId: rowId, werkKey, on } });

  const onParent = (row, on) => {
    for (const b of visible) {
      const info = row.werkeStatus[b.key];
      if (info && info.status !== "identical") onToggle(row.id, b.key, on);
    }
  };

  // Ohne Punkt davor: das farbige Statuswort traegt die Bedeutung selbst,
  // der Dot davor war reine Doppelung (Davids Feedback 27.08.).
  const StatusPill = ({ status }) => {
    const cls = ST_CLS[status] || "idle";
    const label = t("st." + status) || status;
    return <span className={"st st--" + cls}>{label}</span>;
  };

  const refs = state.globalRefs || [];
  // Welche iFlows sind im Transport? Daran haengt, ob eine Referenz gebraucht wird.
  const gewaehlt = useMemo(() => gewaehlteIflows(state.sel), [state.sel]);
  const refsMapped = useMemo(() => refs.map((r) => {
    // Id VOR Name: der Snapshot laedt die Referenz per Id - ein Name-Schluessel liefe bei
    // Name != Id in einen 404. Und package-references schreibt Type GROSS: nur r.type zu
    // lesen zeigte in der Typ-Spalte immer "Reference".
    const id = r?.bundleId || r?.id || r?.Id || r?.name || r?.Name;
    const name = r?.name || r?.Name || r?.bundleId || r?.id || r?.Id || "-";
    const type = r?.type || r?.Type || "Reference";
    const srcVer = r?.version || r?.srcVer || r?.srcVersion || r?.Version || "-";
    const tgtVer = r?.targetVersion || r?.tgtVer || "-";
    let statusRaw = r?.status;
    if (statusRaw) statusRaw = String(statusRaw).toLowerCase();
    else if (r?.targetVersion) statusRaw = r?.version === r?.targetVersion ? "identical" : "update";
    else statusRaw = "new";
    // resolvable=false: Referenz existiert nicht auf dem Quell-Tenant. Der
    // Reducer setzt refSel bereits hart auf false; hier deaktivieren wir die
    // Checkbox zusaetzlich in der UI und zeigen einen klaren Hinweis.
    const resolvable = r?.resolvable !== false;
    // Entwuerfe haben keine aktive Version - der Snapshot kann sie nicht laden und
    // wuerde den ganzen Transport abbrechen. Gleiche Sperre wie bei den iFlows.
    const draft = r?.draft === true || statusRaw === "draft" || String(srcVer).toLowerCase() === "draft";
    // Herkunft: die iFlows, die diese Referenz nutzen (referencedBy). Ist keiner davon im
    // Transport, wird die Referenz nicht gebraucht - und laesst sich auch nicht anwaehlen.
    const nutzer = refNutzer(r);
    const gebraucht = refGebraucht(r, gewaehlt);
    // v10: Typ-Schluessel (MessageMapping | ScriptCollection | ValueMapping | roher bundleType)
    // fuer die Sortierung; das Label in der Typ-Spalte kommt fertig vom Backend.
    const typ = r?.typ || r?.Typ || "";
    // Per Schnittstelle nicht transportierbar (Function Library u. a.): wird gemeldet, die
    // Checkbox bleibt gesperrt, der Grund kommt vom Backend (deutsch) - mit Rueckfalltext.
    const transportierbar = r?.transportierbar !== false;
    const grund = typeof r?.grund === "string" ? r.grund : "";
    // Herkunft "paket": Value Mapping aus dem Quellpaket ohne iFlow-Verweis (referencedBy leer).
    const ausPaket = r?.herkunft === "paket";
    // Value Mapping, das im Ziel schon existiert: der Transport ersetzt dort die gepflegten
    // Werte. Die Checkbox bleibt bedienbar - die Freigabe ist eine bewusste Entscheidung.
    const ueberschreibt = r?.ueberschreibtZielwerte === true;
    return { id, name, type, typ, srcVer, tgtVer, status: statusRaw, resolvable, draft, nutzer, gebraucht, transportierbar, grund, ausPaket, ueberschreibt };
  }).sort((a, b) => {
    // SAP-Deploy-Reihenfolge (Message Mapping -> Script Collection -> Value Mapping, dann
    // Unbekannte), innerhalb alphabetisch - der Nutzer sieht die Reihenfolge des Ziel-Workflows.
    const d = refTypRang(a) - refTypRang(b);
    return d || String(a.name || "").localeCompare(String(b.name || ""));
  }), [refs, gewaehlt]);

  const comparing = stream.running || reloadPending;
  const controlsBusy = comparing || release?.loading === "release";
  const canNext = selectedRowsCount > 0 && !controlsBusy && !stream.error;

  // "Alle auswaehlen" ueber die Kopf-Checkbox: nur wirklich waehlbare Zeilen
  // (nicht identisch, kein Entwurf). indeterminate zeigt eine Teilauswahl an.
  const totalSelectable = useMemo(() => {
    let n = 0;
    for (const row of rows) {
      if (row.draft) continue;
      n += nSelectable(row);
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, visible]);
  const setAllRows = (on) => {
    for (const row of rows) {
      if (row.draft) continue;
      for (const b of visible) {
        const info = row.werkeStatus[b.key];
        if (info && info.status !== "identical") onToggle(row.id, b.key, on);
      }
    }
  };
  // Die Kopf-Checkbox nimmt nur, was sich gefahrlos sammeln laesst: transportierbar, aufloesbar,
  // kein Entwurf, nicht identisch, gebraucht - und KEIN Value Mapping, das Zielwerte
  // ueberschreiben wuerde. Das hakt der Nutzer nur explizit je Zeile an.
  const selectableRefs = useMemo(
    () => refsMapped.filter((r) => r.transportierbar && r.resolvable && !r.draft && r.status !== "identical" && r.gebraucht && !r.ueberschreibt),
    [refsMapped]
  );
  const selectedRefCount = selectableRefs.filter((r) => !!state.refSel[r.id]).length;
  const setAllRefs = (on) => {
    for (const r of selectableRefs) {
      if (!!state.refSel[r.id] !== on) dispatch({ type: "TOGGLE_REF", payload: r.id });
    }
  };
  const headCbRef = (all, some) => (el) => { if (el) el.indeterminate = some && !all; };

  return (
    <section className="scene active scene-enter" id="s3">
      <div className="sh">
        <h2>{t("s3.h2")}</h2>
        <p>{t("s3.p")}</p>
      </div>

      {(comparing || stream.phases.some((p) => p.status === "active")) && (
        <div className="panel panel-pad">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>{t(Object.keys(state.precheckByBucket || {}).length ? "s3.refreshing" : "s3.loading")}</h3>
            <span className="tiny muted">
              {withWerke ? t("s3.par", { n: activeWerke(state).length }) : t("s3.single")}
            </span>
          </div>
          <div>
            {[
              { id: "auth", label: t("s3.ph1") },
              { id: "source", label: t("s3.ph2") },
              { id: "target", label: t("s3.ph3") },
              { id: "refs", label: t("s3.ph4") },
              { id: "diff", label: t("s3.ph5") },
            ].map((ph) => {
              const it = stream.phases.find((x) => x.id === ph.id);
              const st = it?.status || "waiting";
              const cls = st === "done" ? "done" : st === "active" ? "run" : "";
              return (
                <div className={"phase-li " + cls} key={ph.id}>
                  <span className="ph-ind">
                    {st === "active" && <span className="spinner" />}
                    {st === "done" && <svg className="ic ic-16 ph-check"><use href="#ic-check" /></svg>}
                  </span>
                  <span>{ph.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {stream.error && (
        <div className="errline">
          <span style={{ flex: 1 }}>{t("err.precheck")}: {stream.error}</span>
          <button className="btn btn-sec btn-sm" onClick={() => refresh()} disabled={controlsBusy}>{t("err.retry")}</button>
        </div>
      )}

      {Object.keys(state.precheckByBucket || {}).length > 0 && (
        <fieldset disabled={controlsBusy || !!stream.error} aria-busy={comparing} style={{ border: 0, padding: 0, margin: 0, marginTop: comparing ? 16 : 0, minWidth: 0 }}>
          <div className="panel">
            <div className="panel-h artifact-panel-head">
              <div className="artifact-panel-title">
                <h3>{t("s3.tableTitle")}</h3>
                <div className="deployment-guidance">
                  {t(pipeline.modus === "sap" ? "deploy.sap" : "deploy.hint", { stage: String(state.space?.zielStufe || state.space?.qasInstance?.stage || "").toUpperCase() })}
                </div>
              </div>
              <div className="artifact-panel-actions">
                <button className="btn btn-sec btn-sm" onClick={() => refresh()} title={t("s3.refreshTitle")}>
                  <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  {t("s3.refresh")}
                </button>
                <div className="diffcounts">
                  {counts.update > 0 && <span style={{ color: "var(--info)" }}><b>{counts.update}</b> {t("st.update")}</span>}
                  {counts.new > 0 && <span style={{ color: "var(--ok)" }}><b>{counts.new}</b> {t("st.new")}</span>}
                  {counts.identical > 0 && <span className="muted"><b>{counts.identical}</b> {t("st.identical")}</span>}
                  {counts.draft > 0 && <span style={{ color: "var(--warn)" }}><b>{counts.draft}</b> {t("st.draft")}</span>}
                </div>
              </div>
            </div>
            {withWerke && (
              <div style={{ display: "block", padding: "14px 26px 0" }}>
                <div className="utabs" style={{ marginBottom: 0, borderBottom: "none" }}>
                  <button className={"utab" + (state.workingTab === "all" ? " on" : "")} onClick={() => dispatch({ type: "SET_WORKING_TAB", payload: "all" })}>
                    {t("s3.all", { n: bucketList.length })}
                  </button>
                  {bucketList.map((b) => (
                    <button key={b.key} className={"utab" + (state.workingTab === b.key ? " on" : "")} onClick={() => dispatch({ type: "SET_WORKING_TAB", payload: b.key })}>
                      {b.name} <span className="ken">{b.kennung}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="panel-pad tablewrap" style={{ paddingTop: 12 }}>
              <table className="dt transport-table">
                <ArtifactColumns />
                <thead>
                  <tr>
                    <th className="transport-choice">
                      <input
                        type="checkbox"
                        className="cb"
                        aria-label={t("s3.selAll")}
                        title={t("s3.selAll")}
                        checked={totalSelectable > 0 && selectedRowsCount >= totalSelectable}
                        disabled={totalSelectable === 0}
                        ref={headCbRef(selectedRowsCount >= totalSelectable, selectedRowsCount > 0)}
                        onChange={(e) => setAllRows(e.target.checked)}
                      />
                    </th>
                    <th>{t("th.art")}</th>
                    <th>{t("deploy.versions", { cx: <Cx /> })}</th>
                    <th>{t("th.status")}</th>
                    <th>{t("deploy.heading")}</th>
                    <th>{t("th.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    if (row.draft) {
                      const isEditing = release && release.artId === row.id;
                      return (
                        <tr key={row.id}>
                          <td />
                          <td>
                            <span className="art-name">{row.name}</span><br />
                            <span className="art-id">{t("s3.draftLast", { id: row.id, v: row.lastVer })} · {row.type}</span>
                          </td>
                          <td><Versions source={t("st.draft")} /></td>
                          <td><StatusPill status="draft" /></td>
                          <td><span className="transport-no-deploy">{t("deploy.releaseFirst")}</span></td>
                          <td>
                            {!isEditing && (
                              <button className="btn btn-sec btn-sm" onClick={() => openRelease(row)}>{t("s3.release")}</button>
                            )}
                            {isEditing && (
                              <span className="draft-editor">
                                <input
                                  className={"inp" + (release?.error ? " inp--invalid" : "")}
                                  value={release.version}
                                  onChange={(e) => setRelease({ ...release, version: e.target.value, error: null })}
                                  aria-label="Version"
                                />
                                <button className="btn btn-sm btn-sec" onClick={() => doRelease(row)} disabled={release.loading}>
                                  {t("s3.doRel")}
                                </button>
                                <span className="tiny muted">{t("s3.lastVer", { v: release.floor || row.lastVer })}</span>
                                {release.error && <span className="tiny" style={{ color: "var(--crit)" }}>{release.error}</span>}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    if (!withWerke) {
                      const info = row.werkeStatus.std;
                      if (!info) return null;
                      const ident = info.status === "identical";
                      const sel = !!state.sel?.[row.id]?.std;
                      return (
                        <tr key={row.id}>
                          <td className="transport-choice">
                            <input
                              type="checkbox"
                              className="cb"
                              checked={sel}
                              disabled={ident}
                              aria-label={t("deploy.transportLabel", { name: row.name })}
                              onChange={(e) => onToggle(row.id, "std", e.target.checked)}
                            />
                          </td>
                          <td>
                            <span className="art-name">{row.name}</span><br />
                            <span className="art-id">{row.id} · {row.type}</span>
                          </td>
                          <td><Versions source={row.srcVer} target={info.tgt} /></td>
                          <td><StatusPill status={info.status} /></td>
                          <td>{sel ? <DeploymentSwitch name={row.name} checked={autoDeploy(state, row.id, "std")} disabled={!deploymentAvailable}
                            onChange={on => dispatch({ type: "SET_DEPLOY", payload: { sourceId: row.id, werkKey: "std", on } })} />
                            : <span className="transport-no-deploy">{t(ident ? "s3.ident" : "deploy.notSelected")}</span>}</td>
                          <td>
                            {info.status === "update" && (
                              <button className="btn btn-sec btn-sm" onClick={() => openModal(row, "std")}>{t("s3.view")}</button>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    const selectable = nSelectable(row);
                    const chosen = nOn(row);
                    const agg = {};
                    for (const b of visible) {
                      const info = row.werkeStatus[b.key];
                      if (!info) continue;
                      agg[info.status] = (agg[info.status] || 0) + 1;
                    }
                    const aggTxt = Object.entries(agg).map(([s, n]) => `${n} ${t("st." + s)}`).join(" · ");
                    const open = !!state.expanded[row.id];
                    return (
                      <React.Fragment key={row.id}>
                        <tr>
                          <td>
                            <input
                              type="checkbox"
                              className="cb"
                              checked={chosen === selectable && selectable > 0}
                              disabled={selectable === 0}
                              onChange={(e) => onParent(row, e.target.checked)}
                            />
                          </td>
                          <td>
                            <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                              <button
                                className={"exp-btn" + (open ? " open" : "")}
                                onClick={() => dispatch({ type: "TOGGLE_EXPAND", payload: row.id })}
                                aria-label="Werke aufklappen"
                              >
                                <svg className="ic ic-16"><use href="#ic-chevron-right" /></svg>
                              </button>
                              <span>
                                <span className="art-name">{row.name}</span><br />
                                <span className="art-id">{row.id} · {row.type} · {t("s3.plain", { n: visible.length })}</span>
                              </span>
                            </span>
                          </td>
                          <td><Versions source={row.srcVer} /></td>
                          <td><span className="tiny muted">{aggTxt}</span></td>
                          <td />
                          <td />
                        </tr>
                        {open && visible.map((b) => {
                          const info = row.werkeStatus[b.key];
                          if (!info) return null;
                          const ident = info.status === "identical";
                          const sel = !!state.sel?.[row.id]?.[b.key];
                          return (
                            <tr className="childrow" key={row.id + "-" + b.key}>
                              <td className="transport-choice">
                                <input
                                  type="checkbox"
                                  className="cb"
                                  checked={sel}
                                  disabled={ident}
                                  aria-label={t("deploy.transportLabel", { name: row.name + " · " + b.name })}
                                  onChange={(e) => onToggle(row.id, b.key, e.target.checked)}
                                />
                              </td>
                              <td>
                                <span className="tree-cell">
                                  <span>
                                    <span className="art-name">{b.name}</span>
                                    <span className="art-id" style={{ display: "block" }}>
                                      {row.id}<span className="ken">{b.kennung}</span> · {info.suffixedId} · {row.type}
                                    </span>
                                  </span>
                                </span>
                              </td>
                              <td><Versions source={row.srcVer} target={info.tgt} /></td>
                              <td><StatusPill status={info.status} /></td>
                              <td>{sel ? <DeploymentSwitch name={row.name + " · " + b.name} checked={autoDeploy(state, row.id, b.key)} disabled={!deploymentAvailable}
                                onChange={on => dispatch({ type: "SET_DEPLOY", payload: { sourceId: row.id, werkKey: b.key, on } })} />
                                : <span className="transport-no-deploy">{t(ident ? "s3.ident" : "deploy.notSelected")}</span>}</td>
                              <td>
                                {info.status === "update" && (
                                  <button className="btn btn-sec btn-sm" onClick={() => openModal(row, b.key)}>{t("s3.view")}</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {refsMapped.length > 0 && (
            <div className="sect panel">
              <div className="panel-h">
                <h3>{t("s3.refs")}</h3>
                <span className="meta">{t("s3.refsMeta")}</span>
              </div>
              <div className="panel-pad" style={{ paddingTop: 12, paddingBottom: 0 }}>
                <p className="tiny muted" style={{ margin: 0 }}>{t("deploy.refsHint")}</p>
              </div>
              <div className="panel-pad tablewrap" style={{ paddingTop: 12 }}>
                <table className="dt transport-table">
                  <ArtifactColumns />
                  <thead>
                    <tr>
                      <th className="transport-choice">
                        <input
                          type="checkbox"
                          className="cb"
                          aria-label={t("s3.selAll")}
                          title={t("s3.selAll")}
                          checked={selectableRefs.length > 0 && selectedRefCount >= selectableRefs.length}
                          disabled={selectableRefs.length === 0}
                          ref={headCbRef(selectedRefCount >= selectableRefs.length, selectedRefCount > 0)}
                          onChange={(e) => setAllRefs(e.target.checked)}
                        />
                      </th>
                      <th>{t("th.art")}</th>
                      <th>{t("deploy.versions", { cx: <Cx /> })}</th>
                      <th>{t("th.status")}</th>
                      <th>{t("deploy.heading")}</th>
                      <th>{t("th.action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refsMapped.map((r) => (
                      <tr key={r.id}>
                        <td className="transport-choice">
                          <input
                            type="checkbox"
                            className="cb"
                            checked={r.transportierbar && r.resolvable && !r.draft && r.gebraucht && !!state.refSel[r.id]}
                            disabled={r.status === "identical" || !r.transportierbar || !r.resolvable || r.draft || !r.gebraucht}
                            onChange={() => dispatch({ type: "TOGGLE_REF", payload: r.id })}
                            aria-label={t("deploy.transportLabel", { name: r.name })}
                          />
                        </td>
                        <td>
                          <span className="art-name">{r.name}</span>
                          <div className="art-id">{r.type}</div>
                          {r.ausPaket && r.nutzer.length === 0 && (
                            // Value Mapping aus dem Quellpaket ohne iFlow-Verweis: die Herkunft
                            // ist das Paket, nicht ein iFlow - gleiche dezente Zeile wie "aus <iFlow>".
                            <div className="tiny muted ref-from" style={{ marginTop: 2 }}>
                              {t("s3.refFromPkg", { pkg: state.pkgName || state.pkgId || "-" })}
                            </div>
                          )}
                          {r.nutzer.length > 0 && (
                            // Kurzform "aus A, B +n"; bei mehr als zwei Nutzern zeigt Hover oder Fokus
                            // die volle Liste (hoechstens zehn) an Ort und Stelle - ein Overlay wuerde
                            // der Scroll-Container der Tabelle abschneiden.
                            <div
                              className={"tiny muted ref-from" + (r.nutzer.length > 2 ? " ref-from--more" : "")}
                              style={{ marginTop: 2 }}
                              tabIndex={r.nutzer.length > 2 ? 0 : undefined}
                            >
                              <span className="ref-from-short">
                                {t("s3.refFrom", { flows: r.nutzer.slice(0, 2).join(", ") + (r.nutzer.length > 2 ? ` +${r.nutzer.length - 2}` : "") })}
                              </span>
                              {r.nutzer.length > 2 && (
                                <span className="ref-from-all">
                                  {t("s3.refFrom", { flows: r.nutzer.slice(0, 10).join(", ") })}
                                  {r.nutzer.length > 10 && ` ${t("s3.refFromMore", { n: r.nutzer.length - 10 })}`}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td><Versions source={r.srcVer} target={r.tgtVer} /></td>
                        <td><StatusPill status={r.status} /></td>
                        <td>{state.refSel?.[r.id] && r.gebraucht && r.transportierbar && r.resolvable && !r.draft && <DeploymentSwitch name={r.name}
                          checked={referenceAutoDeploy(state, { id: r.id, referencedBy: r.nutzer })} disabled={!deploymentAvailable}
                          required={referenceDeployRequired(state, { referencedBy: r.nutzer })}
                          onChange={on => dispatch({ type: "SET_REF_DEPLOY", payload: { id: r.id, on } })} />}</td>
                        <td>
                          {/* Genau ein Hinweis je Zeile, in der Reihenfolge der Sperren: nicht
                              transportierbar > nicht aufloesbar > Entwurf > identisch > kein iFlow.
                              Erst wenn die Checkbox bedienbar ist, kommt die Warnung vor dem
                              Ueberschreiben der Zielwerte - sie begleitet die bewusste Freigabe. */}
                          {!r.transportierbar && <span className="tiny" style={{ color: "var(--warn)" }}>{r.grund || t("s3.refNotTransportable")}</span>}
                          {r.transportierbar && !r.resolvable && <span className="tiny" style={{ color: "var(--warn)" }}>{t("s3.refUnres")}</span>}
                          {r.transportierbar && r.resolvable && r.draft && <span className="tiny" style={{ color: "var(--warn)" }}>{t("s3.refDraft")}</span>}
                          {r.transportierbar && r.resolvable && !r.draft && r.status === "identical" && <span className="tiny muted">{t("s3.ident")}</span>}
                          {r.transportierbar && r.resolvable && !r.draft && r.status !== "identical" && !r.gebraucht && <span className="tiny muted">{t("s3.refNoFlow")}</span>}
                          {r.transportierbar && r.resolvable && !r.draft && r.status === "update" && r.gebraucht && r.ueberschreibt && <span className="tiny" style={{ color: "var(--warn)" }}>{t("s3.refOverwrites")}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {stream.refsWarning && (
                  <div className="errline" style={{ marginTop: 8 }}>
                    <span style={{ flex: 1 }}>{stream.refsWarning}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </fieldset>
      )}

      <div className="footbar" style={{ flexWrap: "wrap" }}>
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 2 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <span className="foot-hint">
            {comparing ? t("s3.refreshing") : selectedRowsCount > 0 ? t("s3.selN", { n: selectedRowsCount }) : t("s3.min")}
          </span>
          <button className="btn btn-cta" onClick={() => dispatch({ type: "SET_STEP", payload: 4 })} disabled={!canNext}>
            {t("s3.next")}
            <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
          </button>
        </div>
      </div>

      <BpmnCompareDialog
        open={!!modal}
        onClose={() => setModal(null)}
        sourceInstanceId={modal?.sourceInstanceId}
        targetInstanceId={modal?.targetInstanceId}
        artifactId={modal?.artId}
        targetArtifactId={modal?.targetArtId}
        artifactName={modal?.artName}
        sourcePackageId={state.pkgId}
        sourceVersion={modal?.srcVer}
        targetVersion={modal?.tgtVer}
      />
    </section>
  );
}
